import * as layout from '../../components/markLayout.module.css';
import {
    MarkButton,
    MarkChoice,
    MarkInput,
    MarkSelect,
    MarkTextArea,
    MarkLabel
} from '../../components/MarkControls';
import React, { useId, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Tooltip from '@hero/ui-kit/components/Tooltip';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useSegment from '../../api/useSegment';
import useImportSegment from '../../api/useImportSegment';
import useCsvParse from '../../hooks/useCsvParse';
import useUnsavedChangesRegistration from '../../hooks/useUnsavedChangesRegistration';
import type { SegmentTargetKind, SegmentImportResponse } from '../../types';
import { SEGMENT_ERROR_LABELS } from '../../constants';
import UsedByModal from './UsedByModal';
import { getSegmentErrorCode } from './segmentErrors';
import {
    InertContainer,
    segmentLabelStyle,
    segmentPrimaryButtonStyle,
    segmentSecondaryButtonStyle,
    TARGET_KINDS,
    TARGET_KIND_LABELS
} from './segmentForm';
type ImportColumn = 'email' | 'profile_id' | 'hero_user_id' | 'hero_account_id';
const COLUMN_OPTIONS: {
    value: ImportColumn;
    label: string;
}[] = [
    { value: 'email', label: 'Email' },
    { value: 'profile_id', label: 'Profile ID' },
    { value: 'hero_user_id', label: 'Hero User ID' },
    { value: 'hero_account_id', label: 'Hero Account ID' }
];

// Import is one JSON POST; Django's DATA_UPLOAD_MAX_MEMORY_SIZE default (2.5 MB) rejects larger
// bodies with a non-JSON 400 before the view runs.
const MAX_IMPORT_PAYLOAD_BYTES = 2000000;
const getImportErrorMessage = (error: unknown): string => {
    const code = getSegmentErrorCode(error);
    if (code) return SEGMENT_ERROR_LABELS[code] ?? code;
    const response = (
        error as {
            response?: {
                status?: number;
                data?: unknown;
            };
        }
    )?.response;
    const isNonJson = typeof response?.data !== 'object' || response?.data === null;
    if (response?.status === 413 || (response?.status === 400 && isNonJson)) {
        return 'The server rejected the upload, most likely because it is too large (limit about 2 MB). Split the file into smaller imports.';
    }
    return 'Import failed';
};

const ImportSegment: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const formId = useId();
    const existingSegmentId = searchParams.get('segment_id')
        ? Number(searchParams.get('segment_id'))
        : 0;
    const isReplace = existingSegmentId > 0;
    const {
        data: existingResponse,
        isLoading: existingLoading,
        error: existingError,
        refetch: refetchExisting
    } = useSegment(existingSegmentId);
    const existing = existingResponse?.data;
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [name, setName] = useState('');
    const [targetKind, setTargetKind] = useState<SegmentTargetKind | ''>('');
    const [column, setColumn] = useState<ImportColumn>('email');
    const [skipHeader, setSkipHeader] = useState(false);
    const [inputMode, setInputMode] = useState<'file' | 'text'>('file');
    const [textValue, setTextValue] = useState('');
    const [showForceConfirm, setShowForceConfirm] = useState(false);
    const [importResult, setImportResult] = useState<SegmentImportResponse | null>(null);
    const [importComplete, setImportComplete] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const csv = useCsvParse();
    const importMutation = useImportSegment();
    const effectiveTargetKind = isReplace && existing ? existing.target_kind : targetKind;
    const isProspectAccountId = effectiveTargetKind === 'PROSPECT' && column === 'hero_account_id';
    const dirty =
        !importComplete &&
        (!!name || !!targetKind || column !== 'email' || skipHeader || !!csv.source || !!textValue);
    const clearUnsavedChanges = useUnsavedChangesRegistration(
        'segment-import',
        dirty,
        importMutation.isPending
    );
    const canProceedStep1 =
        (isReplace || (!!name.trim() && !!targetKind)) && !!column && !isProspectAccountId;
    const payloadBytes = useMemo(
        () =>
            csv.result
                ? new TextEncoder().encode(JSON.stringify({ rows: csv.result.rows })).length
                : 0,
        [csv.result]
    );
    const payloadTooLarge = payloadBytes > MAX_IMPORT_PAYLOAD_BYTES;
    const hasDelimiterChars = useMemo(
        () => !!csv.result?.rows.some((row) => row.includes(',') || row.includes('"')),
        [csv.result]
    );
    const isReplaceOfQuerySegment = isReplace && !!existing && existing.kind !== 'IMPORTED';
    const canProceedStep2 = !!csv.result && csv.result.totalCount > 0 && !payloadTooLarge;
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setTextValue('');
            csv.parseFile(file, skipHeader);
        } else {
            csv.reset();
        }
    };
    const handleSkipHeaderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextSkipHeader = e.currentTarget.checked;
        setSkipHeader(nextSkipHeader);
        const selectedFile = fileRef.current?.files?.[0];
        if (inputMode === 'file' && selectedFile) {
            csv.parseFile(selectedFile, nextSkipHeader);
        } else if (inputMode === 'text' && textValue.trim() && csv.source === 'text') {
            csv.parseText(textValue, nextSkipHeader);
        } else {
            csv.reset();
        }
    };
    const switchInputMode = (mode: 'file' | 'text') => {
        setInputMode(mode);
        csv.reset();
        if (mode === 'file') {
            setTextValue('');
        } else if (fileRef.current) {
            fileRef.current.value = '';
        }
    };
    const handleParseText = () => {
        if (textValue.trim()) {
            csv.parseText(textValue, skipHeader);
        }
    };
    const handleSubmit = (force: boolean) => {
        if (!csv.result) return;
        const usedByCount = existing
            ? existing.used_by.drips.length + existing.used_by.blasts.length
            : 0;
        if (isReplace && usedByCount > 0 && !force) {
            setShowForceConfirm(true);
            return;
        }
        importMutation.mutate(
            {
                segment_id: isReplace ? existingSegmentId : undefined,
                name: isReplace ? undefined : name.trim(),
                target_kind: isReplace ? undefined : (targetKind as SegmentTargetKind),
                column,
                rows: csv.result.rows,
                force: force || undefined
            },
            {
                onSuccess: (response) => {
                    flushSync(() => {
                        setImportComplete(true);
                        clearUnsavedChanges();
                        setImportResult(response.data);
                        setStep(3);
                        setShowForceConfirm(false);
                    });
                },
                onError: async (error) => {
                    setShowForceConfirm(false);
                    if (isReplace && getSegmentErrorCode(error) === 'segment_in_use') {
                        await refetchExisting();
                        setShowForceConfirm(true);
                    }
                }
            }
        );
    };
    if (isReplace && existingLoading) return <InlineLoader />;
    if (isReplace && existingError) return <QueryInlineError error={existingError} />;
    if (isReplaceOfQuerySegment) {
        return (
            <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
                <H role="h5">{`Replace: ${existing.name}`}</H>
                <P noDefaultMargin>
                    Only imported segments can be replaced via CSV. This is a query segment — edit
                    its filters instead.
                </P>
            </Section>
        );
    }
    const errorMessage = importMutation.error ? getImportErrorMessage(importMutation.error) : null;
    return (
        <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
            <H role="h5">{isReplace ? `Replace: ${existing?.name}` : 'Import CSV Segment'}</H>
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginBottom: '1.2rem'
                }}
            >
                Step {step} of 3 ·{' '}
                {step === 1
                    ? 'Set up your audience'
                    : step === 2
                      ? 'Add and review your contacts'
                      : 'Import results'}
            </P>
            {step === 1 && (
                <InertContainer
                    disabled={importMutation.isPending}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2.4rem',
                        minWidth: 0
                    }}
                >
                    {!isReplace && (
                        <div className={layout.field}>
                            <MarkLabel htmlFor={`${formId}-name`} style={segmentLabelStyle}>
                                Segment name
                            </MarkLabel>
                            <MarkInput
                                id={`${formId}-name`}
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.currentTarget.value)}
                                placeholder="My imported segment"
                            />
                        </div>
                    )}
                    {!isReplace && (
                        <div className={layout.field}>
                            <MarkLabel htmlFor={`${formId}-target`} style={segmentLabelStyle}>
                                Target kind
                            </MarkLabel>
                            <MarkSelect
                                id={`${formId}-target`}
                                value={targetKind}
                                onChange={(e) =>
                                    setTargetKind(e.currentTarget.value as SegmentTargetKind)
                                }
                            >
                                <option value="">Select target kind</option>
                                {TARGET_KINDS.map((kind) => (
                                    <option key={kind} value={kind}>
                                        {TARGET_KIND_LABELS[kind]}
                                    </option>
                                ))}
                            </MarkSelect>
                        </div>
                    )}
                    {isReplace && existing && (
                        <div
                            style={{
                                marginBottom: '1.2rem'
                            }}
                        >
                            <MarkLabel style={segmentLabelStyle}>Target kind</MarkLabel>
                            <P
                                noDefaultMargin
                                styles={{
                                    padding: '0.6rem 1rem'
                                }}
                            >
                                {TARGET_KIND_LABELS[existing.target_kind]} (locked)
                            </P>
                        </div>
                    )}
                    <div className={layout.field}>
                        <MarkLabel htmlFor={`${formId}-column`} style={segmentLabelStyle}>
                            Column
                            {effectiveTargetKind === 'PROSPECT' && (
                                <Tooltip content="Hero Account ID is not supported for Prospect segments.">
                                    <span
                                        tabIndex={0}
                                        aria-label="Hero Account ID is not supported for Prospect segments"
                                        style={{
                                            marginLeft: '0.4rem',
                                            cursor: 'help'
                                        }}
                                    >
                                        ⓘ
                                    </span>
                                </Tooltip>
                            )}
                        </MarkLabel>
                        <MarkSelect
                            id={`${formId}-column`}
                            value={column}
                            onChange={(e) => setColumn(e.currentTarget.value as ImportColumn)}
                        >
                            {COLUMN_OPTIONS.map((opt) => (
                                <option
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={
                                        effectiveTargetKind === 'PROSPECT' &&
                                        opt.value === 'hero_account_id'
                                    }
                                >
                                    {opt.label}
                                    {effectiveTargetKind === 'PROSPECT' &&
                                    opt.value === 'hero_account_id'
                                        ? ' (not supported for Prospect)'
                                        : ''}
                                </option>
                            ))}
                        </MarkSelect>
                        {isProspectAccountId && (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-danger)',
                                    marginTop: '0.4rem'
                                }}
                            >
                                Hero Account ID is not supported for Prospect target kind.
                            </P>
                        )}
                    </div>
                    {isReplace && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)',
                                marginBottom: '1.2rem',
                                fontStyle: 'italic'
                            }}
                        >
                            Column selection is not remembered from the previous import — pick it
                            again to match your CSV.
                        </P>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            marginBottom: '1.2rem'
                        }}
                    >
                        <MarkChoice
                            type="checkbox"
                            checked={skipHeader}
                            onChange={handleSkipHeaderChange}
                            id={`${formId}-header`}
                        />
                        <MarkLabel htmlFor={`${formId}-header`}>
                            First row is a header (skip it)
                        </MarkLabel>
                    </div>
                    <MarkButton
                        variant="primary"
                        type="button"
                        disabled={!canProceedStep1}
                        onClick={() => setStep(2)}
                    >
                        Next
                    </MarkButton>
                </InertContainer>
            )}
            {step === 2 && (
                <InertContainer
                    disabled={importMutation.isPending}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2.4rem',
                        minWidth: 0
                    }}
                >
                    <div
                        style={{
                            marginBottom: '1.2rem'
                        }}
                        className={layout.importInputActions}
                    >
                        <MarkButton
                            aria-pressed={inputMode === 'file'}
                            type="button"
                            onClick={() => switchInputMode('file')}
                        >
                            Upload file
                        </MarkButton>
                        <MarkButton
                            aria-pressed={inputMode === 'text'}
                            type="button"
                            onClick={() => switchInputMode('text')}
                        >
                            Paste text
                        </MarkButton>
                    </div>
                    {inputMode === 'file' && (
                        <div className={layout.field}>
                            <MarkLabel htmlFor={`${formId}-csv-file`} style={segmentLabelStyle}>
                                CSV file
                            </MarkLabel>
                            <MarkInput
                                id={`${formId}-csv-file`}
                                ref={fileRef}
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileChange}
                            />
                        </div>
                    )}
                    {inputMode === 'text' && (
                        <div className={layout.field}>
                            <MarkLabel htmlFor={`${formId}-csv-values`} style={segmentLabelStyle}>
                                CSV values
                            </MarkLabel>
                            <MarkTextArea
                                id={`${formId}-csv-values`}
                                value={textValue}
                                onChange={(e) => {
                                    setTextValue(e.currentTarget.value);
                                    csv.reset();
                                }}
                                placeholder="Paste one value per line"
                                rows={8}
                            />
                            <MarkButton
                                type="button"
                                onClick={handleParseText}
                                disabled={!textValue.trim()}
                                style={{
                                    marginTop: '0.6rem',
                                    alignSelf: 'flex-start'
                                }}
                            >
                                Parse
                            </MarkButton>
                        </div>
                    )}
                    {csv.result && (
                        <div
                            style={{
                                backgroundColor: 'var(--mark-surface)',
                                marginBottom: '1.2rem'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontWeight: 600,
                                    marginBottom: '0.6rem'
                                }}
                            >
                                Parsed {csv.result.totalCount} rows
                            </P>
                            <div
                                style={{
                                    fontFamily: 'monospace',
                                    lineHeight: 1.6
                                }}
                            >
                                {csv.result.preview.map((row, i) => (
                                    <P noDefaultMargin key={i}>
                                        {row}
                                    </P>
                                ))}
                                {csv.result.totalCount > 5 && (
                                    <P
                                        noDefaultMargin
                                        size="small"
                                        styles={{
                                            color: 'var(--mark-muted)',
                                            marginTop: '0.4rem'
                                        }}
                                    >
                                        ...and {csv.result.totalCount - 5} more
                                    </P>
                                )}
                            </div>
                            {hasDelimiterChars && (
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-warning)',
                                        marginTop: '0.6rem'
                                    }}
                                >
                                    Some rows contain commas or quotes. Each line is imported as a
                                    single value, so multi-column CSVs are not split — export one
                                    column only.
                                </P>
                            )}
                            {payloadTooLarge && (
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-danger)',
                                        marginTop: '0.6rem'
                                    }}
                                >
                                    {`Too large to import: ${csv.result.totalCount.toLocaleString()} rows is ${(payloadBytes / 1000000).toFixed(1)} MB, over the ${MAX_IMPORT_PAYLOAD_BYTES / 1000000} MB limit. Split the file into smaller imports.`}
                                </P>
                            )}
                        </div>
                    )}
                    <div className={layout.formFooter}>
                        <MarkButton
                            type="button"
                            onClick={() => setStep(1)}
                            style={segmentSecondaryButtonStyle}
                        >
                            Back
                        </MarkButton>
                        <MarkButton
                            variant="primary"
                            type="button"
                            disabled={!canProceedStep2 || importMutation.isPending}
                            onClick={() => handleSubmit(false)}
                        >
                            {importMutation.isPending ? 'Importing...' : 'Import'}
                        </MarkButton>
                    </div>
                    {errorMessage && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)',
                                marginTop: '0.8rem'
                            }}
                        >
                            {errorMessage}
                        </P>
                    )}
                </InertContainer>
            )}
            {step === 3 && importResult && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2.4rem',
                        minWidth: 0
                    }}
                >
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
                            gap: '1rem',
                            marginBottom: '1.6rem'
                        }}
                    >
                        <div
                            style={{
                                textAlign: 'center',
                                backgroundColor: 'var(--mark-surface)'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontSize: '2.4rem',
                                    fontWeight: 700
                                }}
                            >
                                {importResult.total_rows}
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Total rows
                            </P>
                        </div>
                        <div
                            style={{
                                textAlign: 'center',
                                backgroundColor: 'var(--mark-surface)'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontSize: '2.4rem',
                                    fontWeight: 700,
                                    color: 'var(--mark-success)'
                                }}
                            >
                                {importResult.matched}
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Matched
                            </P>
                        </div>
                        <div
                            style={{
                                textAlign: 'center',
                                backgroundColor: 'var(--mark-surface)'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontSize: '2.4rem',
                                    fontWeight: 700,
                                    color: 'var(--mark-danger)'
                                }}
                            >
                                {importResult.unmatched}
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Unmatched
                            </P>
                        </div>
                        <div
                            style={{
                                textAlign: 'center',
                                backgroundColor: 'var(--mark-surface)'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontSize: '2.4rem',
                                    fontWeight: 700
                                }}
                            >
                                {importResult.skipped_existing}
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Skipped (existing)
                            </P>
                        </div>
                        <div
                            style={{
                                textAlign: 'center',
                                backgroundColor: 'var(--mark-surface)'
                            }}
                            className={layout.panel}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    fontSize: '2.4rem',
                                    fontWeight: 700
                                }}
                            >
                                {importResult.skipped_unresolvable}
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Skipped (unresolvable)
                            </P>
                        </div>
                    </div>
                    <MarkButton
                        type="button"
                        onClick={() => navigate(`/mark/segments/${importResult.segment_id}`)}
                        style={segmentPrimaryButtonStyle}
                    >
                        View segment
                    </MarkButton>
                </div>
            )}
            {showForceConfirm && existing && (
                <UsedByModal
                    usedBy={existing.used_by}
                    isPending={importMutation.isPending}
                    onConfirm={() => handleSubmit(true)}
                    onCancel={() => setShowForceConfirm(false)}
                    action="replace"
                />
            )}
        </Section>
    );
};
export default ImportSegment;
